import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Calendar, TrendingUp, Users, DollarSign, BarChart3 } from 'lucide-react';
import Card from '../ui/Card';

/**
 * Daily Analytics Charts Component
 * Displays usage and profit trends with date range selector
 */
const DailyAnalyticsCharts = () => {
  const [analyticsData, setAnalyticsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    end: new Date().toISOString().split('T')[0] // Today
  });
  const [summaryStats, setSummaryStats] = useState({
    totalContacts: 0,
    totalJobs: 0,
    totalUsers: 0,
    totalRevenue: 0,
    totalProfit: 0,
    avgSuccessRate: 0
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      
      if (!adminToken) {
        console.error('No admin token available');
        return;
      }

      // Add time to dates to ensure full day coverage
      const startDateTime = `${dateRange.start}T00:00:00.000Z`;
      const endDateTime = `${dateRange.end}T23:59:59.999Z`;

      const response = await fetch(
        `${API_BASE_URL}/api/admin/reports/daily-analytics?start_date=${encodeURIComponent(startDateTime)}&end_date=${encodeURIComponent(endDateTime)}`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch analytics data: ${response.status}`);
      }

      const data = await response.json();
      setAnalyticsData(data);
      
      // Calculate summary statistics
      const stats = data.reduce((acc, day) => ({
        totalContacts: acc.totalContacts + day.contacts_scraped,
        totalJobs: acc.totalJobs + day.jobs_total,
        totalUsers: acc.totalUsers + day.active_users,
        totalRevenue: acc.totalRevenue + day.revenue_usd,
        totalProfit: acc.totalProfit + day.profit_usd,
        avgSuccessRate: acc.avgSuccessRate + day.success_rate_percentage
      }), { totalContacts: 0, totalJobs: 0, totalUsers: 0, totalRevenue: 0, totalProfit: 0, avgSuccessRate: 0 });
      
      // Average success rate
      const daysWithData = data.filter(day => day.jobs_total > 0).length;
      stats.avgSuccessRate = daysWithData > 0 ? stats.avgSuccessRate / daysWithData : 0;
      
      setSummaryStats(stats);
      
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange]);

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatTooltipValue = (value, name) => {
    if (name.includes('usd') || name.includes('Revenue') || name.includes('Cost') || name.includes('Profit')) {
      return [`$${value.toFixed(4)}`, name];
    }
    if (name.includes('rate')) {
      return [`${value.toFixed(1)}%`, name];
    }
    return [value.toLocaleString(), name];
  };

  const formatXAxisLabel = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80 bg-gray-200 rounded"></div>
            <div className="h-80 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Daily Analytics Dashboard
            </h1>
            <p className="text-gray-600">
              Track daily usage patterns and profit trends over time
            </p>
          </div>
          
          {/* Date Range Selector */}
          <div className="flex items-center gap-4 bg-white p-4 rounded-lg border border-gray-200">
            <Calendar className="h-5 w-5 text-gray-500" />
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">From:</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => handleDateRangeChange('start', e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">To:</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => handleDateRangeChange('end', e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BarChart3 className="h-8 w-8 text-blue-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Contacts</p>
              <p className="text-2xl font-semibold text-gray-900">
                {summaryStats.totalContacts.toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Jobs</p>
              <p className="text-2xl font-semibold text-gray-900">
                {summaryStats.totalJobs.toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-purple-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Users</p>
              <p className="text-2xl font-semibold text-gray-900">
                {summaryStats.totalUsers.toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DollarSign className="h-8 w-8 text-yellow-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Profit</p>
              <p className="text-2xl font-semibold text-gray-900">
                ${summaryStats.totalProfit.toFixed(2)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Usage Chart */}
        <Card>
          <Card.Header>
            <Card.Title>Daily Usage Trends</Card.Title>
          </Card.Header>
          <Card.Content>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={analyticsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatXAxisLabel}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip formatter={formatTooltipValue} />
                <Legend />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="contacts_scraped" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  name="Contacts Scraped"
                  dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="jobs_completed" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  name="Jobs Completed"
                  dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="active_users" 
                  stroke="#8b5cf6" 
                  strokeWidth={2}
                  name="Active Users"
                  dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card.Content>
        </Card>

        {/* Profit Chart */}
        <Card>
          <Card.Header>
            <Card.Title>Daily Profit Analysis</Card.Title>
          </Card.Header>
          <Card.Content>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={analyticsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatXAxisLabel}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis />
                <Tooltip formatter={formatTooltipValue} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="revenue_usd" 
                  stackId="1"
                  stroke="#22c55e" 
                  fill="#22c55e"
                  fillOpacity={0.6}
                  name="Revenue"
                />
                <Area 
                  type="monotone" 
                  dataKey="cost_usd" 
                  stackId="2"
                  stroke="#ef4444" 
                  fill="#ef4444"
                  fillOpacity={0.6}
                  name="Cost"
                />
                <Line 
                  type="monotone" 
                  dataKey="profit_usd" 
                  stroke="#f59e0b" 
                  strokeWidth={3}
                  name="Net Profit"
                  dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card.Content>
        </Card>
      </div>

      {/* Additional Insights */}
      <div className="mt-8">
        <Card>
          <Card.Header>
            <Card.Title>Key Insights</Card.Title>
          </Card.Header>
          <Card.Content>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Average Daily Volume</h4>
                <p className="text-2xl font-bold text-blue-700">
                  {analyticsData.length > 0 ? Math.round(summaryStats.totalContacts / analyticsData.length).toLocaleString() : 0}
                </p>
                <p className="text-sm text-blue-600">contacts per day</p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-green-900 mb-2">Success Rate</h4>
                <p className="text-2xl font-bold text-green-700">
                  {summaryStats.avgSuccessRate.toFixed(1)}%
                </p>
                <p className="text-sm text-green-600">average job success rate</p>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-medium text-yellow-900 mb-2">Profit Margin</h4>
                <p className="text-2xl font-bold text-yellow-700">
                  {summaryStats.totalRevenue > 0 ? ((summaryStats.totalProfit / summaryStats.totalRevenue) * 100).toFixed(1) : 0}%
                </p>
                <p className="text-sm text-yellow-600">net profit margin</p>
              </div>
            </div>
          </Card.Content>
        </Card>
      </div>
    </div>
  );
};

export default DailyAnalyticsCharts;